import React from "react";
import { IPolicyUpdates } from "../../../../../../redux/policyCommon/policyCommon.types";
import { useFormik } from "formik";
import { RightPanel } from "../../../../../../components/RightPanel";
import {
  Button,
  Grid,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../components/formsComponents";
import { CustomDatePicker } from "../../../../../../components/formsComponents/CustomDatePicker";
import { FileUpload } from "../../../../../../features/FileUpload/components/FileUpload";
import produce from "immer";
import { truncateToDecimals } from "../../../../../../helpers/truncateToDecimals";
import { CSS_CONSTANTS } from "../../../../../../constants/cssConstants";
import { useDispatchWrapper } from "../../../../../../hooks";
import { upsertPolicyUpdatesAsync } from "../../../../../../redux/policyCommon/policyCommonActions";

export interface IPolicyUpdatesRightPanelProps {
  open: boolean;
  data: IPolicyUpdates;
  onComplete: () => void;
  onClose: () => void;
}


export const LIMITS = {
  "Less than $175,000": {
    "$1,000,000 / $2,000,000": { premium: 455 },
    "$1,000,000 / $5,000,000": { premium: 505 },
    "$2,000,000 / $2,000,000": { premium: 605 },
    "$2,000,000 / $5,000,000": { premium: 690 },
  },
  "Greater than $175,000": {
    "$1,000,000 / $2,000,000": { premium: 490 },
    "$1,000,000 / $5,000,000": { premium: 555 },
    "$2,000,000 / $2,000,000": { premium: 655 },
    "$2,000,000 / $5,000,000": { premium: 755 },
  },
};

export const PolicyUpdatesRightPanel: React.FC<
  IPolicyUpdatesRightPanelProps
> = (props) => {
  const { open, data, onComplete, onClose } = props;
  const theme = useTheme();
  const [file, setFile] = React.useState<File | null>(null);
  const dispatch = useDispatchWrapper();

  const {
    values,
    errors,
    setFieldValue,
    handleChange,
    setValues,
    handleSubmit,
  } = useFormik({
    initialValues: data,
    onSubmit: (values) => {
      dispatch(upsertPolicyUpdatesAsync(values,data,values.policy_update_id ? true : false, file, (isSuccess) => {
        if (isSuccess) {
          onComplete();
        }
      }));
    },
  });

  const calcTotal = (values: IPolicyUpdates) => {
    if (values.limit_of_liability && values.gross_revenue) {
      if (values.gross_revenue === "Less than $175,000") {
        values.premium =
          LIMITS["Less than $175,000"][
            values.limit_of_liability as keyof typeof LIMITS["Less than $175,000"]
          ].premium;
      } else {
        values.premium =
          LIMITS["Greater than $175,000"][
            values.limit_of_liability as keyof typeof LIMITS["Greater than $175,000"]
          ].premium;
      }

      if (values.province) {
        const taxRate = parseFloat(values.tax || "0");
        const premium = values.premium || 0;
        const tax = (premium * taxRate) / 100;
        values.total_payable = truncateToDecimals(premium + tax).toString();
      }
    }
  };

  const handleProvinceChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draft) => {
      draft.province = value;
      if (value === "ON") {
        draft.tax = "8";
      } else {
        draft.tax = "0";
      }
      calcTotal(draft);
    });
    setValues(newValues);
  };

  const handleGrossRevenueChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draft) => {
      draft.gross_revenue = value;
      calcTotal(draft);
    });
    setValues(newValues);
  };

  const handleLimitsofLiabilityChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draft) => {
      draft.limit_of_liability = value;

      calcTotal(draft);
    });
    setValues(newValues);
  };

  return (
    <>
      <RightPanel
        heading="Updates"
        open={open}
        onClose={onClose}
        isWrappedWithForm
        onFormSubmit={handleSubmit}
        actionButtons={() => {
          return (
            <Stack spacing={2} direction={"row"} justifyContent="flex-end">
             

              <Button 
                variant="contained" 
                type="submit" 
                size="large"
                startIcon={<CheckCircleOutline />}
               
              >
                Save Changes
              </Button>
            </Stack>
          );
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Named Insured</CustomFormLabel>
            <Typography variant="body1" fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE} fontWeight={600}>
              {values.customer_name || "--"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Policy #</CustomFormLabel>
            <Typography variant="body1" fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE} fontWeight={600}>
              {values.policy_number || "--"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Effective Date</CustomFormLabel>
            <CustomDatePicker
              value={values.effective_date || ""}
              fullWidth
              onChange={(value) => setFieldValue("effective_date", value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Expiry Date</CustomFormLabel>
            <CustomDatePicker
              value={values.expiry_date || ""}
              fullWidth
              onChange={(value) => setFieldValue("expiry_date", value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Province</CustomFormLabel>
            <ControlledCustomSelect
              fullWidth
              value={values.province}
              name="province"
              onChange={handleProvinceChange}
              placeholder="Select One"
              displayEmpty
              options={["AB", "ON"].map((template) => {
                return { label: template, value: template };
              })}
            ></ControlledCustomSelect>
          </Grid>
         
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Gross Revenue</CustomFormLabel>
            <ControlledCustomSelect
              fullWidth
              value={values.gross_revenue}
              name="gross_revenue"
              onChange={handleGrossRevenueChange}
              placeholder="Select One"
              displayEmpty
              options={[
                { label: "Less than $175,000", value: "Less than $175,000" },
                {
                  label: "Greater than $175,000",
                  value: "Greater than $175,000",
                },
              ].map((item) => {
                return { label: item.label, value: item.value };
              })}
            ></ControlledCustomSelect>
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Limits of Liability</CustomFormLabel>
            <ControlledCustomSelect
              fullWidth
              value={values.limit_of_liability}
              name="limit_of_liability"
              onChange={handleLimitsofLiabilityChange}
              placeholder="Select One"
              displayEmpty
              options={[
                { label: "$1,000,000 / $2,000,000", value: "$1,000,000 / $2,000,000" },
                { label: "$1,000,000 / $5,000,000", value: "$1,000,000 / $5,000,000" },
                { label: "$2,000,000 / $2,000,000", value: "$2,000,000 / $2,000,000" },
                { label: "$2,000,000 / $5,000,000", value: "$2,000,000 / $5,000,000" },
              ].map((item) => {
                return { label: item.label, value: item.value };
              })}
            ></ControlledCustomSelect>
          </Grid>
          <Grid item xs={12} md={6}></Grid>
          <Grid item xs={12} md={4} mt={1}>
            <Stack
              direction={"column"}
              sx={{
                border: `1px solid ${theme.palette.success.main}`,
                borderRadius: CSS_CONSTANTS.BORDER_RADIUS,
                p: 1,
                background: theme.palette.success.light,
              }}
            >
              <Typography
                variant="body1"
                fontWeight={500}
                fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
              >
                Premium
              </Typography>

              <Typography
                variant="body1"
                fontWeight={600}
                fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
              >
                {values.premium || "--"}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4} mt={1}>
            <Stack
              direction={"column"}
              sx={{
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: CSS_CONSTANTS.BORDER_RADIUS,
                p: 1,
                background: theme.palette.primary.light,
              }}
            >
              <Typography
                variant="body1"
                fontWeight={500}
                fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
              >
                Tax
              </Typography>

              <Typography
                variant="body1"
                fontWeight={600}
                fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
              >
                {values.tax  ? values.tax+"%": "--"}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4} mt={1}>
                  <Stack
              direction={"column"}
              sx={{
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: CSS_CONSTANTS.BORDER_RADIUS,
                p: 1,
                background: theme.palette.error.light,
              }}
            >
              <Typography
                variant="body1"
                fontWeight={500}
                fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
              >
                Total Payable
              </Typography>

              <Typography
                variant="body1"
                fontWeight={600}
                fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
              >
                {values.total_payable || "--"}
              </Typography>
            </Stack>
            
          </Grid>
          <Grid item xs={12} md={12}>
            <CustomFormLabel>Link for Payment</CustomFormLabel>
            <Typography variant="body1" fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE} fontWeight={600}>
              {values.link_for_payment || "--"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={12}>
            <CustomFormLabel>Attachment</CustomFormLabel>
            <FileUpload
           
              value={values.attachment}
              onChange={(file) => {
                setFile(file);
              }}
              onDelete={() => {
                setFile(null);
                setValues({
                  ...values,
                  attachment: null,
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Status</CustomFormLabel>
            <ControlledCustomSelect
              fullWidth
              value={values.status}
              name="status"
              onChange={handleChange}
              placeholder="Select One"
              displayEmpty
              options={["ACTIVE", "INACTIVE", "Submitted"].map((template) => {
                return { label: template, value: template };
              })}
            ></ControlledCustomSelect>
          </Grid>
        </Grid>
      </RightPanel>
    </>
  );
};
